import React, { useState, useRef, useEffect } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import MarkdownMath from '../MarkdownMath';
import './Modules.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

type ResponseLang = 'en' | 'zh-hant' | 'zh-hans';

// One reference file at a time; size cap matches backend MAX_UPLOAD_MB (25 MB).
const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ACCEPT_ATTR = '.txt,.pdf,.ppt,.pptx,.png,.jpg,.jpeg';
const VISION_EXTS = new Set(['.pdf', '.ppt', '.pptx', '.png', '.jpg', '.jpeg']);
const TEXT_EXTS = new Set(['.txt']);

const extOf = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

// Progress curve: start at 8% and ease asymptotically toward 90% so the bar
// keeps moving for the whole (unbounded) vision read but never completes until
// the server responds. (Same pattern as the Notes module.)
const PROGRESS_HEAD_START = 8;
const PROGRESS_CEILING = 90;
const PROGRESS_TIME_CONSTANT_MS = 20000;

const statusForProgress = (pct: number): string => {
  if (pct < 40) return 'Reading your file with AI vision…';
  if (pct < 70) return 'Transcribing pages, diagrams and formulas…';
  if (pct < 85) return 'Preparing the material as chat reference…';
  return 'Still working — long files take a little longer…';
};

const UnderstandingModule: React.FC = () => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
  const [processingTime, setProcessingTime] = useState<string>('');

  // The single reference file. Its extracted/vision-read text is kept in state
  // only (never displayed) and sent to the tutor as `context` on every question.
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [referenceText, setReferenceText] = useState('');
  const [pageMeta, setPageMeta] = useState<{ read?: number; total?: number }>({});
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const [responseLang, setResponseLang] = useState<ResponseLang>('en');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Tracks the previous message count so the chat panel only auto-scrolls when
  // a genuinely NEW message arrives (not on mount or after "New Chat").
  const prevMessageCountRef = useRef(0);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressStartRef = useRef<number>(0);

  const stopProgress = () => {
    if (progressTimerRef.current !== null) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const startProgress = () => {
    stopProgress();
    progressStartRef.current = Date.now();
    setProgress(PROGRESS_HEAD_START);
    setStatusMessage(statusForProgress(PROGRESS_HEAD_START));
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      const pct = PROGRESS_HEAD_START +
        (PROGRESS_CEILING - PROGRESS_HEAD_START) *
        (1 - Math.exp(-elapsed / PROGRESS_TIME_CONSTANT_MS));
      setProgress(pct);
      setStatusMessage(statusForProgress(pct));
    }, 200);
  };

  useEffect(() => stopProgress, []);

  // Read the chosen file once: documents/images go through the DeepSeek vision
  // model (mode=vision); plain .txt is decoded directly (fast, embedded text).
  const readReferenceFile = async (file: File) => {
    setIsReadingFile(true);
    setPageMeta({});
    startProgress();

    const form = new FormData();
    form.append('file', file);
    form.append('mode', VISION_EXTS.has(extOf(file.name)) ? 'vision' : 'text');

    try {
      const res = await apiRequest<{
        extracted_text?: string;
        page_count?: number;
        total_pages?: number;
        error?: string;
      }>(API_ENDPOINTS.UPLOAD_DOCUMENT, {
        method: 'POST',
        body: form,
        timeout: 20 * 60 * 1000,
      });

      const text = (res.data?.extracted_text || '').trim();
      if (!res.data || !text) {
        alert(res.data?.error || 'Could not read any content from that file. Please try another file.');
        setReferenceFile(null);
        setReferenceText('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setReferenceText(text);
      setPageMeta({ read: res.data.page_count, total: res.data.total_pages });
      setProgress(100);
    } catch (err) {
      console.error('Reference file read failed', err);
      const serverMsg = err instanceof Error ? err.message : '';
      alert(
        (serverMsg && serverMsg !== 'API request failed' ? `${serverMsg}\n\n` : '') +
          'Could not read that file. Please make sure it is valid and try again.'
      );
      setReferenceFile(null);
      setReferenceText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      stopProgress();
      setIsReadingFile(false);
      setStatusMessage('');
    }
  };

  // Stage a chosen/dropped file — replaces any previous file (one file only).
  const stageFile = (file: File) => {
    const ext = extOf(file.name);
    if (!TEXT_EXTS.has(ext) && !VISION_EXTS.has(ext)) {
      alert('Only TXT, PDF, PPT/PPTX, PNG or JPG files are accepted.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('File exceeds the 25 MB limit.');
      return;
    }
    setReferenceFile(file);
    setReferenceText('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    readReferenceFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) stageFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (isReadingFile || isSending) return;
    const file = e.dataTransfer.files?.[0];
    if (file) stageFile(file);
  };

  const removeReferenceFile = () => {
    setReferenceFile(null);
    setReferenceText('');
    setPageMeta({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // REVAMP: conversations are not saved — "New Chat" resets the view completely.
  const startNewChat = () => {
    setMessages([]);
    setInputMessage('');
    removeReferenceFile();
  };

  const handleSendMessage = async () => {
    const question = inputMessage.trim();
    if (!question || isSending || isReadingFile) return;

    const newMessage: ChatMessage = { role: 'user', content: question };
    const history = [...messages, newMessage];
    setMessages(history);
    setInputMessage('');

    setIsSending(true);
    setProcessingStartTime(Date.now());
    setProcessingTime('0s');

    try {
      const response = await apiRequest<{ reply?: string; error?: string }>(
        API_ENDPOINTS.UNDERSTANDING_CHAT,
        {
          method: 'POST',
          body: JSON.stringify({
            context: referenceText,
            history,
            language: responseLang,
          }),
        }
      );

      const reply = response.data?.reply;
      if (reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      } else {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: `Sorry, an error occurred: ${response.data?.error || 'Unknown error'}` },
        ]);
      }
    } catch (err) {
      console.error('Chat error', err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Network error. Please try again later.' }]);
    } finally {
      setIsSending(false);
      setProcessingStartTime(null);
      setProcessingTime('');
    }
  };

  // Elapsed-time ticker while the tutor is thinking.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isSending && processingStartTime) {
      interval = setInterval(() => {
        const elapsed = Date.now() - processingStartTime;
        const minutes = Math.floor(elapsed / 60000);
        const seconds = Math.floor((elapsed % 60000) / 1000);
        setProcessingTime(minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`);
      }, 1000);
    } else {
      setProcessingTime('');
    }
    return () => {
      if (interval!) clearInterval(interval);
    };
  }, [isSending, processingStartTime]);

  // Only the CHAT PANEL scrolls (never the whole window via scrollIntoView),
  // and only when a genuinely new message arrives while the view is pinned to
  // the bottom — entering the page, typing or pressing "New Chat" won't move it.
  const stickToBottomRef = useRef(true);

  const handleChatScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const grew = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (!grew || messages.length === 0) return;
    // Follow the user's own message, or the AI reply when they were at the bottom.
    const lastIsUser = messages[messages.length - 1]?.role === 'user';
    if (lastIsUser || stickToBottomRef.current) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const pageLabel = () => {
    if (typeof pageMeta.read !== 'number') return null;
    const total = pageMeta.total ?? pageMeta.read;
    return ` · read ${pageMeta.read} of ${total} page${total !== 1 ? 's' : ''}`;
  };

  return (
    <div className="module-container understanding-module">
      {/* Header */}
      <div className="module-header">
        <span className="module-icon">🧠</span>
        <h2 className="module-title">{t('moduleHeaders.understanding.title')}</h2>
        <p className="module-subtitle">{t('moduleHeaders.understanding.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="understanding" />

      <div className="module-content">
        <div className="content-section">
          <div className="section-header">
            <h3>AI Chat</h3>
            <p>Attach one reference file (optional), then ask anything about it.</p>

            <ul className="chat-guidelines">
              <li>One file at a time: TXT, PDF, PPT/PPTX, PNG or JPG, up to 25 MB.</li>
              <li>Files are visually read with AI (text, diagrams and formulas), then consulted on every question you ask.</li>
              <li>Reading image-heavy or long files can take a while — please wait for the ✓ before chatting.</li>
              <li>Select your preferred response language from the dropdown above the chat.</li>
              <li>Click "New Chat" anytime to start a fresh conversation (this chat is kept in your tab only).</li>
            </ul>
          </div>

          {/* Single unified upload box */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept={ACCEPT_ATTR}
          />

          {!referenceFile && (
            <div
              className={`file-upload${isDragging ? ' dragover' : ''}`}
              onClick={() => !isReadingFile && fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <span className="file-upload-icon">📎</span>
              <div className="file-upload-text">Drag &amp; drop your file here</div>
              <div className="file-upload-hint">or click to browse — TXT, PDF, PPT, PNG, JPG</div>
              <span className="file-upload-button">Choose File</span>
            </div>
          )}

          {referenceFile && (isReadingFile || referenceText) && (
            <div className="reference-file-chip">
              <span className="reference-file-name">
                {isReadingFile ? '⏳' : '✅'} {referenceFile.name}
                {!isReadingFile && pageLabel()}
              </span>
              <button
                type="button"
                className="reference-file-remove"
                onClick={removeReferenceFile}
                disabled={isReadingFile}
                title="Remove this file"
              >
                ✕
              </button>
            </div>
          )}

          {isReadingFile && (
            <div className="progress-container" style={{ marginTop: '12px' }}>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${Math.round(progress)}%` }}></div>
              </div>
              <div className="progress-message">
                {statusMessage} {Math.round(progress)}%
              </div>
            </div>
          )}

          {/* Language selector above chat */}
          <div style={{ margin: '12px 8px 12px 0', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Response language:</span>
            <select
              className="language-dropdown"
              value={responseLang}
              onChange={(e) => setResponseLang(e.target.value as ResponseLang)}
            >
              <option value="en">English</option>
              <option value="zh-hant">繁體中文</option>
              <option value="zh-hans">简体中文</option>
            </select>
          </div>

          {/* Chat */}
          <div className="chat-container">
            <div className="chat-messages" ref={messagesContainerRef} onScroll={handleChatScroll}>
              {messages.map((message, index) => (
                <div key={index} className={`message ${message.role}`}>
                  <div className="message-avatar">
                    {message.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="message-content">
                    {message.role === 'assistant' ? (
                      <MarkdownMath content={message.content} />
                    ) : (
                      <div>{message.content}</div>
                    )}
                  </div>
                </div>
              ))}
              {isSending && (
                <div className="message assistant">
                  <div className="message-avatar">🤖</div>
                  <div className="message-content">
                    <div>Thinking...</div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '4px',
                      fontStyle: 'italic'
                    }}>
                      Processing time: {processingTime}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="chat-input">
              <button className="btn btn-secondary" onClick={startNewChat}>
                New Chat
              </button>
              <input
                type="text"
                placeholder="Type your question..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
              />
              <button
                className="btn btn-primary"
                onClick={handleSendMessage}
                disabled={isSending || isReadingFile || !inputMessage.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnderstandingModule;
